import { inject, Injectable, signal } from '@angular/core';
import { RequestEntitiesService } from './request-entities.service';
import { RequestsApiService } from './requests-api.service';
import {
  LogisticRequestRatingActionEnum,
  LogisticRequestRatingList,
  RatingsStatus,
} from '@core/types';
import { map, Observable, switchMap, tap } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class RequestQcRatingsService extends RequestEntitiesService<
  LogisticRequestRatingList[]
> {
  private readonly requestsApiService = inject(RequestsApiService);
  public $ratingsStatus = signal<RatingsStatus>({});
  readonly $request = this.requestService.$request;

  readonly source$ = (): Observable<LogisticRequestRatingList[]> => {
    return this.requestsApiService.getQCRatings$().pipe(
      tap(ratings => {
        const ratingsStatus: RatingsStatus = {};
        const currentReqRatingsMap = new Map(
          this.$request()
            ?.ratingsWithDetails?.filter(rating => rating.type === 2)
            .map(rating => [rating.id, rating]),
        );

        ratings.forEach(rating => {
          ratingsStatus[rating.id] = currentReqRatingsMap.has(rating.id);
        });
        this.$ratingsStatus.set(ratingsStatus);
      }),
      map(ratings => ratings.sort((a, b) => b.level - a.level)), // Sort `level` in descending order;
    );
  };

  public toggleRating(
    ratingId: LogisticRequestRatingList['id'],
    action: LogisticRequestRatingActionEnum,
  ): Observable<void> {
    return this.requestService.requestId$.pipe(
      switchMap(requestId =>
        this.requestsApiService.toggleRequestRating$(
          requestId,
          ratingId,
          action,
        ),
      ),
    );
  }

  public updateRatingQCComment$(rating: LogisticRequestRatingList) {
    return this.requestService.requestId$.pipe(
      switchMap(requestId =>
        this.requestsApiService.updateRequestRatingQCComment$(
          requestId,
          rating,
        ),
      ),
    );
  }
}
